import { createContext, useContext, useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

const DashboardThemeContext = createContext({ theme: "light", toggle: () => {} });

export function DashboardThemeProvider({ storageKey = "pawlinx-dashboard-theme", defaultTheme = "light", children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    return localStorage.getItem(storageKey) || defaultTheme;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, theme);
    // Toggle Tailwind's `dark` class on <html> so dark: variants work everywhere.
    if (typeof document !== "undefined") {
      const html = document.documentElement;
      if (theme === "dark") html.classList.add("dark");
      else html.classList.remove("dark");
    }
  }, [theme, storageKey]);

  // Cleanup when this provider unmounts (e.g. customer leaves admin shell)
  useEffect(() => () => {
    if (typeof document !== "undefined") document.documentElement.classList.remove("dark");
  }, []);

  const value = {
    theme,
    toggle: () => setTheme((t) => (t === "light" ? "dark" : "light")),
    setTheme,
  };

  return (
    <DashboardThemeContext.Provider value={value}>
      <div className={theme === "dark" ? "dark" : ""}>{children}</div>
    </DashboardThemeContext.Provider>
  );
}

export const useDashboardTheme = () => useContext(DashboardThemeContext);

export function ThemeToggleButton({ className = "" }) {
  const { theme, toggle } = useDashboardTheme();
  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 px-3 h-9 rounded-lg bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 border border-black/10 dark:border-white/10 text-paw-text dark:text-white font-manrope text-xs transition-colors ${className}`}
      data-testid="theme-toggle"
      title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
    >
      {theme === "light" ? <Moon className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Sun className="w-3.5 h-3.5" strokeWidth={1.5} />}
      <span className="hidden sm:inline">{theme === "light" ? "Dark" : "Light"} mode</span>
    </button>
  );
}
